import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActionsEn, siteNavItemsEn } from "@/app/content-en";
import { EN_ALL_FAQS, EN_FAQ_GROUPS } from "@/app/seo-faq-en";

const groupAccents: Record<string, string> = {
  "what-is": "#A3E635",
  "vs-others": "#8B5CF6",
  "opc-business": "#F97316",
  "trust-security": "#EF4444",
  "open-source": "#14B8A6",
  engineering: "#3B82F6",
  "billing-and-governance": "#FACC15",
};

export default function EnglishFaqPage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItemsEn} actionItems={siteHeaderActionsEn} homeHref="/en/" />

      <ContentHero
        eyebrow="FAQ · ENGLISH"
        eyebrowColor="#A3E635"
        titleTop="Questions"
        titleBottom="answered directly"
        bottomColor="#A3E635"
        lead="This English FAQ is optimized for direct retrieval by search engines and answer engines. Each answer is written as a compact, reusable explanation of the product model."
        meta={[`${EN_ALL_FAQS.length} answers`, "FAQPage schema", "English mirror"]}
        breadcrumbs={[
          { label: "English Home", href: "/en/" },
          { label: "FAQ" },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <nav
          className="mb-10 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          aria-label="FAQ topics"
        >
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">Topic index</p>
          <ul className="mt-4 flex flex-wrap gap-3">
            {EN_FAQ_GROUPS.map((group) => (
              <li key={group.id}>
                <a
                  href={`#group-${group.id}`}
                  className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 text-xs font-black uppercase text-black transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
                  style={{ boxShadow: `4px 4px 0 0 ${groupAccents[group.id] ?? "#A3E635"}` }}
                >
                  {group.label}
                  <span className="text-[10px] font-black text-zinc-500">{group.items.length}</span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-12">
          {EN_FAQ_GROUPS.map((group) => {
            const accent = groupAccents[group.id] ?? "#A3E635";
            return (
              <section key={group.id} id={`group-${group.id}`} className="scroll-mt-24">
                <header className="border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                  <div className="flex items-center gap-3">
                    <span className="h-6 w-6 border-2 border-black" style={{ backgroundColor: accent }} aria-hidden />
                    <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">{group.label}</h2>
                  </div>
                  <p className="mt-3 text-sm font-bold leading-6 text-zinc-700 sm:text-base">{group.intro}</p>
                </header>

                <div className="mt-6 grid gap-4">
                  {group.items.map((item, index) => {
                    const id = `${group.id}-${index}`;
                    return (
                      <article
                        key={id}
                        id={id}
                        className="scroll-mt-24 border-4 border-black bg-white/85 p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:p-6"
                      >
                        <h3 className="text-base font-black text-black sm:text-lg">{item.question}</h3>
                        <p className="mt-3 text-sm font-bold leading-7 text-zinc-800 sm:text-base">{item.answer}</p>
                      </article>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
